import { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { formatCurrency } from '../data';
import {
  archiveProduct,
  deleteProductPermanently,
  getAdminProducts,
  getArchivedProducts,
  getCategoriesAdmin,
  restoreProduct
} from '../services/adminManagementService';
import { updateProductListField } from '../services/adminProductListService';
import ProductModal from './ProductModal';
import './admin-products-list.css';

const { FiAlertTriangle, FiArchive, FiEdit3, FiPlus, FiRefreshCw, FiStar, FiTrash2 } = FiIcons;

const productDefaults = {
  name: '',
  slug: '',
  description: '',
  price: '',
  category_id: '',
  status: 'active',
  featured: false,
  quantity_on_hand: 10,
  low_stock_threshold: 2,
  media: [],
  colors: []
};

function ProductRow({ product, archived, busy, onEdit, onArchive, onRestore, onDelete, onToggle }) {
  const media = product.media?.find((item) => item.is_primary) || product.media?.[0];
  const stock = product.inventory?.[0];
  const available = Math.max(
    0,
    Number(stock?.quantity_on_hand || 0)
      - Number(stock?.quantity_reserved || 0)
      - Number(stock?.quantity_committed || 0)
  );
  const colors = product.variants?.filter((variant) => (
    variant.color || variant.attributes?.color
  )).length || 0;

  return (
    <tr className="product-list-row" aria-busy={busy}>
      <td data-label="Product">
        <div className="product-cell">
          {media?.media_url
            ? <img src={media.media_url} alt={product.name} />
            : <div className="img-placeholder">ZEVI</div>}
          <div>
            <strong>{product.name}</strong>
            <small>{product.media?.length || 0} media</small>
          </div>
        </div>
      </td>
      <td data-label="Category">{product.category?.name || 'Uncategorized'}</td>
      <td data-label="Price"><strong>{formatCurrency(product.price)}</strong></td>
      <td data-label="Colors">
        <span className="stock-badge">
          {colors} color{colors !== 1 ? 's' : ''}
        </span>
      </td>
      <td data-label="Stock">
        <span className={`stock-badge ${available === 0 ? 'out' : available <= 2 ? 'low' : 'ok'}`}>
          {available} units
        </span>
      </td>
      {archived ? (
        <td data-label="Archived">
          <small style={{ color: 'var(--admin-muted)' }}>
            {product.archived_at ? new Date(product.archived_at).toLocaleDateString() : '—'}
          </small>
        </td>
      ) : (
        <>
          <td data-label="Status">
            <button
              type="button"
              className={`status-pill ${product.status}`}
              aria-label={`Product is ${product.status}. Click to ${product.status === 'active' ? 'make draft' : 'make active'}.`}
              aria-pressed={product.status === 'active'}
              title="Click to switch between Active and Draft"
              disabled={busy}
              onClick={() => onToggle(product, 'status', product.status === 'active' ? 'draft' : 'active')}
            >
              {product.status === 'active' ? 'Active' : 'Draft'}
            </button>
          </td>
          <td data-label="Homepage">
            <button
              type="button"
              className={`table-action-btn homepage-feature-toggle ${product.featured ? 'is-featured' : ''}`}
              aria-pressed={Boolean(product.featured)}
              disabled={busy}
              onClick={() => onToggle(product, 'featured', !product.featured)}
            >
              <SafeIcon icon={FiStar} />
              <span>{product.featured ? 'Featured' : 'Not featured'}</span>
            </button>
          </td>
        </>
      )}
      <td className="text-right" data-label="Actions">
        <div className="table-btn-group">
          {archived ? (
            <>
              <button type="button" className="table-action-btn edit" onClick={() => onRestore(product)} title="Restore to active">
                <SafeIcon icon={FiRefreshCw} /><span>Restore</span>
              </button>
              <button type="button" className="table-action-btn delete-permanent" onClick={() => onDelete(product)} title="Delete permanently">
                <SafeIcon icon={FiAlertTriangle} /><span>Delete</span>
              </button>
            </>
          ) : (
            <>
              <button type="button" className="table-action-btn edit" onClick={() => onEdit(product)}>
                <SafeIcon icon={FiEdit3} /><span>Edit</span>
              </button>
              <button type="button" className="table-action-btn delete" onClick={() => onArchive(product)} title="Archive this product">
                <SafeIcon icon={FiTrash2} />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function AdminProductsList() {
  const [products, setProducts] = useState([]);
  const [archived, setArchived] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [categoryFilter, setCategoryFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [activeProducts, archivedProducts, categoryRows] = await Promise.all([
        getAdminProducts(),
        getArchivedProducts(),
        getCategoriesAdmin()
      ]);
      setProducts(activeProducts);
      setArchived(archivedProducts);
      setCategories(categoryRows);
    } catch {
      setMessage('Failed to load products.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (values) => {
    const { saveProduct } = await import('../services/adminManagementService');
    await saveProduct(values);
    await load();
  };

  const handleToggle = async (product, field, value) => {
    setBusyId(product.id);
    setMessage('');
    try {
      await updateProductListField(product.id, field, value);
      const updateRow = (rows) => rows.map((row) => (
        row.id === product.id ? { ...row, [field]: value } : row
      ));
      setProducts(updateRow);
      setArchived(updateRow);
      setMessage(
        field === 'status'
          ? `${product.name} is now ${value}.`
          : `${product.name} is ${value ? 'featured on' : 'no longer featured on'} the homepage.`
      );
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusyId('');
    }
  };

  const handleArchive = async (product) => {
    if (!window.confirm(`Archive "${product.name}"? It will move to the Archive tab.`)) return;
    try {
      await archiveProduct(product.id);
      setMessage(`"${product.name}" has been archived.`);
      await load();
    } catch (error) {
      setMessage(error.message);
    }
  };

  const handleRestore = async (product) => {
    if (!window.confirm(`Restore "${product.name}"? It will return to the active products list.`)) return;
    try {
      await restoreProduct(product.id);
      setMessage(`"${product.name}" has been restored.`);
      await load();
    } catch (error) {
      setMessage(error.message);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`PERMANENTLY DELETE "${product.name}"?\n\nThis cannot be undone. All associated media, variants, and inventory will also be removed.`)) return;
    try {
      await deleteProductPermanently(product.id);
      setMessage(`"${product.name}" has been permanently deleted.`);
      await load();
    } catch (error) {
      setMessage(error.message);
    }
  };

  const isArchive = activeTab === 'archive';
  const source = isArchive ? archived : products;
  const visibleProducts = categoryFilter
    ? source.filter((product) => product.category_id === categoryFilter)
    : source;

  return (
    <div className="admin-overview-container">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Products &amp; Stock</h1>
          <p className="admin-page-subtitle">Manage your saree collection, prices, colors, and stock.</p>
        </div>
        {!isArchive && (
          <button className="admin-btn-primary" onClick={() => setEditing({ ...productDefaults })}>
            <SafeIcon icon={FiPlus} /> Add New Saree
          </button>
        )}
      </div>

      <div className="products-tab-bar">
        <button className={`products-tab ${!isArchive ? 'active' : ''}`} onClick={() => setActiveTab('active')}>
          <SafeIcon icon={FiPlus} /><span>Active Products</span><span className="products-tab-count">{products.length}</span>
        </button>
        <button className={`products-tab ${isArchive ? 'active' : ''}`} onClick={() => setActiveTab('archive')}>
          <SafeIcon icon={FiArchive} /><span>Archive</span><span className="products-tab-count">{archived.length}</span>
        </button>
      </div>

      <label className="admin-field-label products-category-filter">
        Filter by category
        <select className="admin-select-pill" value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
          <option value="">All categories</option>
          {categories.map((category) => (
            <option value={category.id} key={category.id}>{category.name}</option>
          ))}
        </select>
      </label>

      {message && <div className="admin-alert info" role="status">{message}</div>}

      {loading ? (
        <div className="admin-loading-state">Loading products…</div>
      ) : visibleProducts.length === 0 ? (
        <div className="admin-empty-state">
          {source.length === 0
            ? isArchive ? 'No archived products.' : 'No products found. Click "Add New Saree" to start.'
            : 'No products match this category.'}
        </div>
      ) : (
        <div className="admin-product-table-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Product</th><th>Category</th><th>Price</th><th>Colors</th><th>Stock</th>
                {isArchive ? <th>Archived</th> : <><th>Status</th><th>Homepage</th></>}
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleProducts.map((product) => (
                <ProductRow
                  key={product.id}
                  product={product}
                  archived={isArchive}
                  busy={busyId === product.id}
                  onEdit={setEditing}
                  onArchive={handleArchive}
                  onRestore={handleRestore}
                  onDelete={handleDelete}
                  onToggle={handleToggle}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ProductModal
          categories={categories}
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          onCreateCategory={async (name) => {
            const slug = name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            const { saveCategory } = await import('../services/adminManagementService');
            await saveCategory({
              name, slug, description: '', active: true, sort_order: categories.length + 1
            });
            const refreshed = await getCategoriesAdmin();
            setCategories(refreshed);
            return refreshed.find((category) => category.slug === slug);
          }}
        />
      )}
    </div>
  );
}